import { useEffect, useState } from 'react'
import LoginScreen from './screens/LoginScreen'
import PosScreen from './screens/PosScreen'
import ReceiptsScreen from './screens/ReceiptsScreen'
import { getUser, getMeta } from './db'
import { fetchDrugs, ApiError } from './api'
import { replaceDrugs, setMeta } from './db'
import { startFlushScheduler } from './outbox'
import toast from 'react-hot-toast'

/**
 * Top-level router for the PWA. Three screens:
 *   login    — when there's no valid session
 *   pos      — the main register
 *   receipts — sales history (re-share PDFs); reached from a top-bar button
 *              in PosScreen, backs out to 'pos'
 *
 * On boot:
 *   1. Look for a persisted session
 *   2. If found and not expired, try to refresh the drugs payload (best-effort)
 *   3. Render the POS screen with cached drugs even if the refresh failed
 *      (this is the whole point of being offline-first)
 */
export default function App() {
  const [phase, setPhase] = useState('boot') // 'boot' | 'login' | 'pos' | 'receipts'
  const [user, setUser] = useState(null)
  const [snapshotAt, setSnapshotAt] = useState(null)
  const [pharmacyName, setPharmacyName] = useState('')

  // Boot
  useEffect(() => {
    (async () => {
      startFlushScheduler()

      const u = await getUser()
      if (!u || !u.token || (u.token_exp && Date.now() > u.token_exp)) {
        setPhase('login')
        return
      }
      setUser(u)
      setPharmacyName((await getMeta('pharmacy_name')) || '')
      setSnapshotAt(await getMeta('snapshot_at'))
      setPhase('pos')

      // Best-effort drugs refresh.
      try {
        const res = await fetchDrugs()
        await replaceDrugs(res.drugs || [])
        if (res.snapshot_at) {
          await setMeta('snapshot_at', res.snapshot_at)
          setSnapshotAt(res.snapshot_at)
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // session died → bounce to login
          setPhase('login')
        } else if (err instanceof ApiError && err.code === 'NETWORK') {
          // expected during outage; the cached drugs still work.
        } else {
          console.warn('drugs refresh failed:', err)
        }
      }
    })()
  }, [])

  const handleLoggedIn = async ({ user: u, pharmacy_name, snapshot_at }) => {
    setUser(u)
    setPharmacyName(pharmacy_name || '')
    setSnapshotAt(snapshot_at)
    setPhase('pos')

    // Bootstrap drugs immediately after login. Failure here is non-fatal
    // (the user can still see the empty cart UI and we'll retry on next open).
    try {
      const res = await fetchDrugs()
      await replaceDrugs(res.drugs || [])
      if (res.snapshot_at) {
        await setMeta('snapshot_at', res.snapshot_at)
        setSnapshotAt(res.snapshot_at)
      }
    } catch (err) {
      toast.error('Could not download drugs. Try again when you are online.')
      console.warn('drugs bootstrap failed:', err)
    }
  }

  const handleLoggedOut = () => {
    setUser(null)
    setPhase('login')
  }

  if (phase === 'boot') {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 text-sage-600 text-sm font-medium" role="status">
        <img src={`${import.meta.env.BASE_URL}icon-192.png`} alt="" className="w-14 h-14 rounded-2xl shadow-lg shadow-brand-800/20" />
        Opening the register…
      </div>
    )
  }

  if (phase === 'login') {
    return <LoginScreen onLoggedIn={handleLoggedIn} />
  }

  if (phase === 'receipts') {
    return <ReceiptsScreen onBack={() => setPhase('pos')} />
  }

  return (
    <PosScreen
      user={user}
      pharmacyName={pharmacyName}
      snapshotAt={snapshotAt}
      onLoggedOut={handleLoggedOut}
      onOpenReceipts={() => setPhase('receipts')}
    />
  )
}
